import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileAlt,
  faUserTie,
  faEnvelope,
  // faStar, // Assuming similarityScore might be less prominent for already qualified
  // faCloudUploadAlt, // Removed
  faTrash,
  faSpinner,
  faExclamationTriangle,
  faSearch,
  faSort,
  faSortUp,
  faSortDown,
  faCalendarAlt,
  // faChartBar,
  faListAlt,
  faEdit,
  faSave,
  faTimes,
  faClipboardList,
  faFilter,
  faUser,
  faPaperPlane,
  faBriefcase,
  faUserCheck, // Icon for qualified candidates
} from '@fortawesome/free-solid-svg-icons';
import { backend_url } from '../config';
import '../QualifiedCandidates.css'; // Reusing the same CSS, create a new one if needed

function QualifiedCandidates() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const stageFilter = queryParams.get('stage');

  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: 'name', // Default sort by name or perhaps a 'qualifiedDate' if you add it
    direction: 'ascending',
  });
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [interviewStatus, setInterviewStatus] = useState('');
  const [interviewNote, setInterviewNote] = useState('');
  const [nextInterviewDate, setNextInterviewDate] = useState('');
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [currentCandidateNotes, setCurrentCandidateNotes] = useState([]);
  const [currentCandidateName, setCurrentCandidateName] = useState('');
  const [showFilterOptions, setShowFilterOptions] = useState(false);
  const [statusFilter, setStatusFilter] = useState(stageFilter || 'All');
  const [selectedCandidates, setSelectedCandidates] = useState([]); // For bulk actions like delete or mail

  // Interview stage colors for visual representation (can be the same)
  const stageColors = {
    'Not Started': '#808080',
    'Resume Screening': '#a5d6a7',
    'Phone Interview': '#81c784',
    'Technical Assessment': '#66bb6a',
    'On-site Interview': '#4caf50',
    'Reference Check': '#43a047',
    'Offer Extended': '#2e7d32',
    Hired: '#1b5e20',
    Rejected: '#d32f2f',
    // Add any stages specific to qualified candidates if needed
  };

  // Available interview stages (can be the same or adjusted)
  const interviewStages = [
    'Not Started', // Or a more relevant starting stage for qualified candidates
    'Resume Screening',
    'Phone Interview',
    'Technical Assessment',
    'On-site Interview',
    'Reference Check',
    'Offer Extended',
    'Hired',
    'Rejected',
  ];

  useEffect(() => {
    fetchQualifiedCandidates();
  }, [stageFilter]); // Reload if stageFilter in URL changes

  useEffect(() => {
    if (stageFilter) {
      setStatusFilter(stageFilter);
      setShowFilterOptions(true);
    }
  }, [stageFilter]);

  const fetchQualifiedCandidates = async () => {
    try {
      setLoading(true);
      // Fetch from the /qualified-candidates endpoint
      const response = await fetch(`${backend_url}/qualified-candidates`);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch qualified candidates: ${response.status}`
        );
      }

      const data = await response.json();
      // Ensure data is an array, if not, handle appropriately or expect an array
      setCandidates(
        Array.isArray(data)
          ? data
          : data.data && Array.isArray(data.data)
          ? data.data
          : []
      );
      setError(null);
    } catch (err) {
      console.error('Error fetching qualified candidates:', err);
      setError('Failed to load qualified candidates. Please try again.');
      setCandidates([]); // Set to empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCandidate = async (candidateId) => {
    if (
      !window.confirm(
        'Are you sure you want to remove this candidate from the qualified list?'
      )
    ) {
      return;
    }

    try {
      const response = await fetch(
        `${backend_url}/qualified-candidates/${candidateId}`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete qualified candidate');
      }

      setCandidates(
        candidates.filter((candidate) => candidate._id !== candidateId)
      );
    } catch (err) {
      console.error('Error deleting qualified candidate:', err);
      setError('Failed to delete qualified candidate. Please try again.');
    }
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const startEditingCandidate = (candidate) => {
    setEditingCandidate(candidate._id);
    setInterviewStatus(candidate.interviewStatus || 'Not Started');
    setInterviewNote('');
    setNextInterviewDate(
      candidate.nextInterviewDate
        ? new Date(candidate.nextInterviewDate).toISOString().split('T')[0] // For datetime-local input
        : ''
    );
  };

  const cancelEditing = () => {
    setEditingCandidate(null);
    setInterviewStatus('');
    setInterviewNote('');
    setNextInterviewDate('');
  };

  const saveInterviewStatus = async (candidateId) => {
    try {
      const response = await fetch(
        `${backend_url}/qualified-candidates/${candidateId}/interview-status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            interviewStatus,
            note: interviewNote,
            nextInterviewDate: nextInterviewDate || null,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || 'Failed to update interview status'
        );
      }

      const updatedCandidateData = await response.json();
      // Assuming backend returns { candidate: updatedDoc } or just updatedDoc
      const candidateToUpdate =
        updatedCandidateData.candidate || updatedCandidateData;

      setCandidates(
        candidates.map((candidate) =>
          candidate._id === candidateId ? candidateToUpdate : candidate
        )
      );
      cancelEditing();
    } catch (err) {
      console.error('Error updating interview status:', err);
      setError('Failed to update interview status. Please try again.');
    }
  };

  const viewCandidateNotes = (candidate) => {
    setCurrentCandidateNotes(candidate.interviewNotes || []);
    setCurrentCandidateName(candidate.name);
    setShowNotesModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not scheduled';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const sortedCandidates = [...candidates].sort((a, b) => {
    // Handle cases where sort key might not exist on some objects
    const valA = a[sortConfig.key] || '';
    const valB = b[sortConfig.key] || '';

    if (valA < valB) {
      return sortConfig.direction === 'ascending' ? -1 : 1;
    }
    if (valA > valB) {
      return sortConfig.direction === 'ascending' ? 1 : -1;
    }
    return 0;
  });

  const statusFilteredCandidates =
    statusFilter === 'All'
      ? sortedCandidates
      : sortedCandidates.filter(
          (candidate) =>
            candidate.interviewStatus === statusFilter ||
            (!candidate.interviewStatus && statusFilter === 'Not Started')
        );

  const filteredCandidates = statusFilteredCandidates.filter((candidate) => {
    const nameMatch = candidate.name
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());
    const emailMatch = candidate.email
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());
    const summaryMatch = candidate.resumeSummary
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());
    return nameMatch || emailMatch || summaryMatch;
  });

  const getSortIcon = (name) => {
    if (sortConfig.key === name) {
      return sortConfig.direction === 'ascending' ? faSortUp : faSortDown;
    }
    return faSort;
  };

  const sendIndividualMail = (candidate) => {
    const subject = encodeURIComponent(
      `Regarding Your Application - ${candidate.name}`
    );
    const body = encodeURIComponent(
      `Hi ${candidate.name},\n\n[Your message here regarding their qualified status or next steps]\n\nBest regards,\nTeam CoreHire`
    );
    const mailtoLink = `mailto:${candidate.email}?subject=${subject}&body=${body}`;
    window.open(mailtoLink, '_blank');
  };

  const sendBulkMailToSelected = () => {
    if (selectedCandidates.length === 0) {
      alert('No candidates selected to send mail.');
      return;
    }
    const selectedCandidateObjects = candidates.filter((c) =>
      selectedCandidates.includes(c._id)
    );
    if (selectedCandidateObjects.length === 0) {
      alert('Could not find details for selected candidates.');
      return;
    }

    const bccList = selectedCandidateObjects
      .map((candidate) => candidate.email)
      .join(',');
    const subject = encodeURIComponent(
      'Update on Your Application with CoreHire'
    );
    const body = encodeURIComponent(
      `Dear Candidate,\n\n[Your bulk message here regarding qualified status or next steps for selected group]\n\nBest regards,\nTeam CoreHire`
    );
    const mailtoLink = `mailto:?bcc=${bccList}&subject=${subject}&body=${body}`;
    window.open(mailtoLink, '_blank');
  };

  const sendCalendarInvite = (candidate) => {
    const subject = `Interview Invitation: ${candidate.name} with CoreHire`;
    const bodyHTML = `
      <div style="font-family: Arial, sans-serif; color: #333;">
        <h2 style="color: #5e42a6;">Hi ${candidate.name},</h2>
        <p>We'd like to invite you for an interview. Please find the details attached.</p>
        <p>Looking forward to speaking with you!</p>
        <p style="margin-top: 30px;">Thanks,<br><strong>Team CoreHire</strong></p>
      </div>
    `;
    const outlookCalendarLink = `https://outlook.office.com/calendar/0/deeplink/compose?path=/calendar/action/compose&to=${encodeURIComponent(
      candidate.email
    )}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
      bodyHTML
    )}`;
    window.open(outlookCalendarLink, '_blank');
  };

  const toggleCandidateSelection = (candidateId) => {
    setSelectedCandidates((prevSelected) =>
      prevSelected.includes(candidateId)
        ? prevSelected.filter((id) => id !== candidateId)
        : [...prevSelected, candidateId]
    );
  };

  const selectAllCandidates = () => {
    const allCandidateIds = filteredCandidates.map(
      (candidate) => candidate._id
    );
    setSelectedCandidates(allCandidateIds);
  };

  const clearAllSelections = () => {
    setSelectedCandidates([]);
  };

  return (
    <div className='candidates-container'>
      <div className='candidates-header'>
        <h2 className='candidates-title'>
          <FontAwesomeIcon icon={faUserCheck} /> Qualified Candidates{' '}
          {/* Changed icon and title */}
        </h2>
      </div>

      <div className='search-filter-row'>
        <div className='search-container'>
          <FontAwesomeIcon icon={faSearch} className='search-icon' />
          <input
            type='text'
            placeholder='Search qualified candidates...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className='search-input'
          />
        </div>
        <div className='filter-container'>
          <button
            className={`filter-button ${
              statusFilter !== 'All' ? 'active' : ''
            }`}
            onClick={() => setShowFilterOptions(!showFilterOptions)}
          >
            <FontAwesomeIcon icon={faFilter} />
            {statusFilter !== 'All' ? ` ${statusFilter}` : ' Filter by Status'}
          </button>
          <div className='candidate-count'>
            {filteredCandidates.length} candidates found
          </div>
        </div>
      </div>

      <div className='bulk-actions'>
        <button
          className='action-button'
          onClick={selectAllCandidates}
          disabled={filteredCandidates.length === 0}
        >
          Select All
        </button>
        <button
          className='action-button'
          onClick={clearAllSelections}
          disabled={selectedCandidates.length === 0}
        >
          Clear Selection
        </button>
        {/* Removed "Save Selected" button. Add other bulk actions if needed, e.g., bulk delete or bulk status update */}
        <button
          className='action-button'
          onClick={sendBulkMailToSelected}
          disabled={selectedCandidates.length === 0}
        >
          <FontAwesomeIcon icon={faEnvelope} /> Mail Selected
        </button>
      </div>

      <div
        className='filter-options'
        style={{ display: showFilterOptions ? 'flex' : 'none' }}
      >
        <div
          className={`filter-pill ${statusFilter === 'All' ? 'active' : ''}`}
          onClick={() => setStatusFilter('All')}
        >
          All
        </div>
        {interviewStages.map((stage) => (
          <div
            key={stage}
            className={`filter-pill ${statusFilter === stage ? 'active' : ''}`}
            onClick={() => setStatusFilter(stage)}
          >
            {stage}
          </div>
        ))}
      </div>

      {loading ? (
        <div className='loading-container'>
          <FontAwesomeIcon icon={faSpinner} spin className='loading-icon' />
          <p>Loading qualified candidates...</p>
        </div>
      ) : error ? (
        <div className='error-container'>
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className='error-icon'
          />
          <p>{error}</p>
          <button className='action-button' onClick={fetchQualifiedCandidates}>
            Retry
          </button>
        </div>
      ) : filteredCandidates.length === 0 ? (
        <div className='empty-container'>
          <FontAwesomeIcon icon={faUserCheck} className='empty-icon' />
          <p>No qualified candidates found.</p>
          {
            searchTerm || statusFilter !== 'All' ? (
              <button
                className='action-button'
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('All');
                }}
              >
                Clear Filters
              </button>
            ) : null /* Removed "Upload Resumes" link */
          }
        </div>
      ) : (
        <table className='candidates-table'>
          <thead>
            <tr>
              <th>Select</th>
              <th onClick={() => handleSort('name')}>
                <FontAwesomeIcon icon={faUserTie} /> Name
                <FontAwesomeIcon
                  icon={getSortIcon('name')}
                  className='sort-icon'
                />
              </th>
              <th onClick={() => handleSort('email')}>
                <FontAwesomeIcon icon={faEnvelope} /> Email
                <FontAwesomeIcon
                  icon={getSortIcon('email')}
                  className='sort-icon'
                />
              </th>
              <th onClick={() => handleSort('interviewStatus')}>
                <FontAwesomeIcon icon={faListAlt} /> Status
                <FontAwesomeIcon
                  icon={getSortIcon('interviewStatus')}
                  className='sort-icon'
                />
              </th>
              <th>
                <FontAwesomeIcon icon={faBriefcase} /> Role
              </th>
              <th>
                <FontAwesomeIcon icon={faCalendarAlt} /> Next Interview
              </th>
              <th>
                Actions
                {/* Consider if a general bulk mail for ALL displayed qualified candidates is needed here */}
                {/* <button className="action-icon" onClick={() => sendBulkMail(filteredCandidates)} title="Send Bulk Mail to All Displayed">
                  <FontAwesomeIcon icon={faEnvelope} />
                </button> */}
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredCandidates.map((candidate) => (
              <React.Fragment key={candidate._id}>
                <tr>
                  <td>
                    <input
                      type='checkbox'
                      checked={selectedCandidates.includes(candidate._id)}
                      onChange={() => toggleCandidateSelection(candidate._id)}
                    />
                  </td>
                  <td className='name'>{candidate.name}</td>
                  <td>{candidate.email}</td>
                  <td>
                    <div className='status-cell'>
                      <span
                        className='status-pill'
                        style={{
                          backgroundColor:
                            stageColors[
                              candidate.interviewStatus || 'Not Started'
                            ],
                        }}
                      >
                        {candidate.interviewStatus || 'Not Started'}
                      </span>
                    </div>
                  </td>
                  <td>{candidate.roleName || 'N/A'}</td>
                  <td>{formatDate(candidate.nextInterviewDate)}</td>
                  <td className='action-buttons'>
                    <Link
                      to={`/candidates/${candidate._id}`}
                      state={{ fromQualified: true }}
                      className='profile-button'
                      title='View Profile'
                    >
                      <FontAwesomeIcon icon={faUser} />
                    </Link>
                    <button
                      className='edit-button'
                      onClick={() => startEditingCandidate(candidate)}
                      title='Update Status'
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    {candidate.interviewNotes &&
                      candidate.interviewNotes.length > 0 && (
                        <button
                          className='notes-button'
                          onClick={() => viewCandidateNotes(candidate)}
                          title='View Notes'
                        >
                          <FontAwesomeIcon icon={faClipboardList} />
                        </button>
                      )}
                    <button
                      className='delete-button'
                      onClick={() => handleDeleteCandidate(candidate._id)}
                      title='Remove from Qualified'
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                    <button
                      className='action-icon'
                      onClick={() => sendIndividualMail(candidate)}
                      title='Send Individual Mail'
                    >
                      <FontAwesomeIcon icon={faPaperPlane} />
                    </button>
                    <button
                      className='action-icon'
                      onClick={() => sendCalendarInvite(candidate)}
                      title='Send Calendar Invite'
                    >
                      <FontAwesomeIcon icon={faCalendarAlt} />
                    </button>
                  </td>
                </tr>
                {editingCandidate === candidate._id && (
                  <tr>
                    <td colSpan='7'>
                      {' '}
                      {/* Adjusted colSpan */}
                      <div className='edit-row'>
                        <div className='form-group'>
                          <label className='form-label'>
                            Interview Status:
                          </label>
                          <select
                            value={interviewStatus}
                            onChange={(e) => setInterviewStatus(e.target.value)}
                            className='form-select'
                          >
                            {interviewStages.map((stage) => (
                              <option key={stage} value={stage}>
                                {stage}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className='form-group'>
                          <label className='form-label'>Add Note:</label>
                          <textarea
                            value={interviewNote}
                            onChange={(e) => setInterviewNote(e.target.value)}
                            placeholder='Enter notes about this interview stage...'
                            className='form-textarea'
                          />
                        </div>
                        <div className='form-group'>
                          <label className='form-label'>
                            Next Interview Date:
                          </label>
                          <input
                            type='datetime-local'
                            value={nextInterviewDate}
                            onChange={(e) =>
                              setNextInterviewDate(e.target.value)
                            }
                            className='form-input'
                          />
                        </div>
                        <div className='button-group'>
                          <button
                            className='cancel-button'
                            onClick={cancelEditing}
                          >
                            <FontAwesomeIcon icon={faTimes} /> Cancel
                          </button>
                          <button
                            className='save-button'
                            onClick={() => saveInterviewStatus(candidate._id)}
                          >
                            <FontAwesomeIcon icon={faSave} /> Save
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}

      {showNotesModal && (
        <div className='modal-overlay'>
          <div className='modal-content'>
            <div className='modal-header'>
              <div className='modal-title'>
                Notes for {currentCandidateName}
              </div>
              <button
                className='modal-close-button'
                onClick={() => setShowNotesModal(false)}
              >
                ×
              </button>
            </div>
            <div>
              {currentCandidateNotes.length > 0 ? (
                currentCandidateNotes.map((note, index) => (
                  <div key={index} className='note-item'>
                    <div className='note-header'>
                      <span className='note-stage'>{note.stage}</span>
                      <span className='note-date'>
                        {formatDate(note.createdAt)}
                      </span>
                    </div>
                    <div>{note.note}</div>
                  </div>
                ))
              ) : (
                <p>No notes available.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default QualifiedCandidates;
